import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MButton,
  MDropdownActionItem,
  MDropdownActions,
  MFlex,
  MPageHeader,
  MText,
} from '../../../components/Monetize';
import { ROUTES } from '../../../constants';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import { useDocumentHead } from '../../../services/documentHead';
import { useFlags } from '../../../services/launchDarkly';
import CatalogTabs from './CatalogTabs';

interface DropdownMenuItem {
  label: string;
  enabled: boolean;
  onClick: () => void;
  testId?: string;
}

interface DropDownMenuItemProps {
  children: ReactNode;
}

const DropDownMenuItem = ({ children }: DropDownMenuItemProps) => (
  <MText color="inherit">{children}</MText>
);

const PageHeader = () => {
  const navigate = useNavigate();
  const { setDocTitle } = useDocumentHead();
  const { canCreateSettings } = useSettingsReadOnlyRule();
  const { enableTokenCredits } = useFlags();

  useEffect(() => {
    setDocTitle('Product Catalog');
  }, [setDocTitle]);

  const menuItems: DropdownMenuItem[] = [
    {
      label: 'Product',
      enabled: true,
      onClick: () => navigate(ROUTES.PRODUCT_NEW),
    },
    {
      label: 'Offering',
      enabled: true,
      onClick: () => navigate(ROUTES.OFFERING_NEW),
    },
    {
      label: 'Discount',
      enabled: true,
      onClick: () => navigate(ROUTES.DISCOUNT_NEW),
    },
    {
      label: 'Usage Type',
      enabled: true,
      onClick: () => navigate(ROUTES.USAGE_TYPE_NEW),
    },
    {
      label: 'Bucket',
      enabled: enableTokenCredits,
      onClick: () => navigate(ROUTES.BUCKET_NEW),
    },
  ];

  const renderDropdownTrigger = () => (
    <MButton variant="primary" ml="3.5" data-testid="new-product-catalog-btn">
      New
    </MButton>
  );

  const renderDropdownItems = () =>
    menuItems
      .filter((item) => item.enabled)
      .map((item) => (
        <MDropdownActionItem key={item.label} onClick={item.onClick}>
          <DropDownMenuItem>{item.label}</DropDownMenuItem>
        </MDropdownActionItem>
      ));

  if (!canCreateSettings) {
    return <CatalogTabs featureFlags={{ enableTokenCredits }} />;
  }

  return (
    <>
      <MPageHeader title="Product Catalog" data-testid="product-catalog-title">
        <MFlex>
          <MDropdownActions
            popOverContentProps={{ minW: '120px' }}
            menuButtonProps={{ asChild: true }}
            renderTrigger={renderDropdownTrigger}
          >
            {renderDropdownItems()}
          </MDropdownActions>
        </MFlex>
      </MPageHeader>
      <CatalogTabs featureFlags={{ enableTokenCredits }} />
    </>
  );
};

export default PageHeader;
